<script lang="ts" setup>
import { useLoginSearchFilter } from "@/composables/useLoginSearchFilter"
import type { FilterFieldGetter, LoginSearchFilterConfiguration } from "@/pages/App/Staff/config"
import type { Entity, ModuleConfiguration } from "@/vf"
import { injectMandatory } from "@/vf"
import type { ListState } from "@/vf/components/crud/list/types"
import type { ListFilterItem } from "@/vf/module-configuration/types"
import { truncate } from "@sentry/utils"
import { computed, inject, type Ref, ref, unref, useTemplateRef } from "vue"
import { useI18n } from "vue-i18n"
import { Multiselect as VueMultiselect } from "vue-multiselect"

const props = defineProps<{
    field: Entity
    badge?: number
}>()

const i18n = useI18n()

const items = ref<ListFilterItem[]>(
    (props.field.items ?? []).map((listFilterItem: ListFilterItem) => ({
        name: i18n.t(listFilterItem.name),
        value: listFilterItem.value,
    })),
)

const config = unref(
    injectMandatory<ModuleConfiguration>(
        "module-config",
        "VfListSearch must be used inside an element that provides module-config like VfEdit or VfShow.",
    ),
)

const formControlWithIcon = useTemplateRef("formControlWithIcon")

const filterFieldsGetter = inject<FilterFieldGetter | undefined>("filter-fields-getter", undefined)

const loginSearchFilterConfiguration = inject<LoginSearchFilterConfiguration | undefined>(
    "login-search-filter-config",
    undefined,
)
const listId = computed(() => loginSearchFilterConfiguration?.id ?? config.list?.id())
const loginSearchFilter = loginSearchFilterConfiguration?.enabled ? useLoginSearchFilter() : undefined
loginSearchFilter?.init(listId.value)
const type = computed<"select" | "button">(() =>
    items.value.length === 2 && items.value[0].value === "0" && items.value[1].value === "1" ? "button" : "select",
)
const buttonActive = computed(() => {
    return props.field.field in listState.filter
})

if (filterFieldsGetter && filterFieldsGetter[props.field.field]) {
    filterFieldsGetter[props.field.field]().then((response: ListFilterItem[]) => {
        items.value = response
        loading.value = false
    })
}

const loading: Ref<boolean> = ref(!!(filterFieldsGetter && filterFieldsGetter[props.field.field]))

const listState = injectMandatory<ListState>(
    "list-state",
    "VfListSearch must be used inside an element that provides list-state like VfList.",
)

const listStateFilterMulti = computed({
    get: () => {
        if (listState?.filter && listState?.filter[props.field.field]) {
            return Object.entries(listState.filter[props.field.field]).map(([, listStateValue]) => {
                const item = items.value.find(({ value }) => value === listStateValue)
                return {
                    value: listStateValue,
                    name: item?.name ?? listStateValue,
                }
            })
        }
        return []
    },
    set: value => {
        if (listState && !listState?.filter) {
            listState.filter = {}
        }
        if (listState && !listState?.filter[props.field.field]) {
            listState.filter[props.field.field] = []
        }
        if (listState?.filter && listState?.filter[props.field.field]) {
            listState.filter[props.field.field] = value.map(item => item.value)
        }
    },
})

const listStateFilterSingle = computed({
    get: () => {
        if (listState?.filter && listState?.filter[props.field.field]) {
            Object.entries(listState.filter[props.field.field]).forEach(([, value]) => {
                return value
            })
        }
        return undefined
    },
    set: (value: string) => {
        listState.filter[props.field.field] = [value]
    },
})

function toggle() {
    if (buttonActive.value) {
        delete listState.filter[props.field.field]
    } else {
        listState.filter[props.field.field] = ["1"]
    }
}
</script>

<template>
    <button
        v-if="type === 'button'"
        type="button"
        class="btn btn-shape-rounded btn-shape-rounded-white"
        @click="toggle()"
        :class="buttonActive ? 'btn-primary-dark' : 'btn-primary'"
    >
        <i class="fa fa-fw" :class="field.withIcon"></i>
        <span>
            {{ $t(field.name) }}
        </span>
        <b
            class="button-badge badge rounded-pill text-white"
            :class="buttonActive ? 'badge-primary-dark' : 'badge-primary'"
            v-if="badge"
        >
            {{ badge }}
        </b>
    </button>
    <div
        v-else
        class="form-control-with-icon d-inline-flex"
        :class="{ 'form-control-with-icon': field.withIcon, 'form-control-without-icon': !field.withIcon }"
        ref="formControlWithIcon"
    >
        <i class="fas" :class="field.withIcon" v-if="field.withIcon"></i>
        <template v-if="field.multiSelect">
            <VueMultiselect
                v-model="listStateFilterMulti"
                :options="items"
                :placeholder="$t(field.name)"
                label="name"
                track-by="value"
                :multiple="true"
                open-direction="bottom"
                @open="formControlWithIcon?.classList.add('active')"
                @close="formControlWithIcon?.classList.remove('active')"
                @keydown.space.stop
                @keyup.space.stop
                :disabled="loading"
            >
                <template #selection="{ values }">
                    <span class="multiselect__placeholder" v-if="values.length > 0">
                        {{
                            truncate(
                                values
                                    .map((value: any) => {
                                        return value.name
                                    })
                                    .join(","),
                                12,
                            )
                        }}
                    </span>
                    <span class="badge rounded-pill badge-primary" v-if="values.length > 0">
                        {{ values.length }}
                    </span>
                </template>
            </VueMultiselect>
        </template>
        <template v-else>
            <select class="form-control w-auto" v-model="listStateFilterSingle">
                <option :value="undefined">[ {{ $t(field.name) }} ]</option>
                <option v-for="item in items" :value="item.value" :key="item.value + item.name">
                    {{ item.name }}
                </option>
            </select>
        </template>
    </div>
</template>
<style scoped>
.btn {
    position: relative;
}

/*
 * overlaps the right edge of the button near the top. `.btn` is needed to beat Bootstrap's `.btn .badge`
 * (position: relative), which has the same specificity otherwise and wins depending on the CSS load order
 */
.btn .button-badge {
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
}
</style>
